import { signal } from '@angular/core';

/**
 * The "are you sure?" state machine behind a payment row's delete action:
 * one payment at a time can be pending confirmation, and any number can be
 * mid-flight (processing) while their backend delete call is in progress.
 * Previously duplicated field-for-field in the sales and purchase payment
 * modals. No Angular DI — plain state, so each modal just does
 * `private readonly deleteState = new PaymentDeleteConfirmationState();`
 * and delegates its own (unchanged) public methods to it, keeping templates
 * untouched.
 */
export class PaymentDeleteConfirmationState {
  readonly pendingId = signal<number | null>(null);
  private readonly processingIds = signal<ReadonlySet<number>>(new Set());

  isPendingConfirmation(id: number): boolean {
    return this.pendingId() === id;
  }

  isProcessing(id: number): boolean {
    return this.processingIds().has(id);
  }

  request(id: number): void {
    this.pendingId.set(id);
  }

  cancel(): void {
    this.pendingId.set(null);
  }

  beginProcessing(id: number): void {
    this.processingIds.update((current) => new Set(current).add(id));
  }

  /** Marks `id` as no longer processing only — leaves the pending
   * confirmation untouched either way. */
  stopProcessing(id: number): void {
    this.processingIds.update((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }

  /** Marks `id` as no longer processing, and clears it as pending if it was. */
  endProcessing(id: number): void {
    this.stopProcessing(id);
    if (this.pendingId() === id) {
      this.pendingId.set(null);
    }
  }
}
